import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useConversation } from '../../context/ConversationProvider';
import { usePresence } from '../../context/UserPresenceProvider';
import ConversationList from '../ConversationList';
import ChatWindow from '../ChatWindow';
import ChatErrorBoundary from './ChatErrorBoundary';
import { IconSearch, IconUsers } from '@tabler/icons-react';
import { showErrorToast } from '../../../../utils/toastUtils';
import useChatUser from '../../hooks/useChatUser';
import useUserSearch from '../../hooks/useUserSearch';
import OnlineStatusDot from '../shared/OnlineStatusDot';
import ForwardModal from '../shared/ForwardModal';
import './FloatingChatManager.css';


// Separate component to handle active chat logic and hooks
const ActiveChatWindowWrapper = ({ activeConversationId, conversations, currentUser, handleCloseChat }) => {
    const { presenceByUserId } = usePresence();
    const { membersByConversation } = useConversation();
    const activeConv = (conversations || []).find(c => c.id === activeConversationId) || null;
    const isGroup = activeConv && activeConv.type === 'group';

    // Group chat member info
    const groupMembers = isGroup ? (membersByConversation?.[activeConversationId] || []) : [];
    const memberCount = isGroup
        ? (groupMembers.length || (Array.isArray(activeConv?.memberIds) ? activeConv.memberIds.length : 0))
        : 0;

    // Calculate online members for groups
    const onlineMemberCount = isGroup
        ? (activeConv?.memberIds || []).filter(id => presenceByUserId?.[id]?.isOnline).length
        : 0;

    // Direct chat user logic
    const directMemberIds = !isGroup && activeConv ? (Array.isArray(activeConv.memberIds) ? activeConv.memberIds : []) : [];
    const otherUserId = !isGroup && currentUser?.uid ? directMemberIds.find(id => id !== currentUser.uid) : null;
    const { user: directUser, isOnline } = useChatUser(otherUserId);

    return (
        <div className="floating-chat-window-wrapper">
            <div className="floating-chat-window-header">
                {isGroup ? (
                    <div className="floating-chat-header-group">
                        <div className="floating-chat-header-group-avatar">
                            <IconUsers size={20} stroke={1.5} />
                        </div>
                        <div className="floating-chat-header-details">
                            <h3 className="floating-chat-header-name">
                                {activeConv?.title || 'Group Chat'}
                            </h3>
                            <div className="floating-chat-header-status">
                                <span className="floating-chat-header-member-count">
                                    {memberCount} member{memberCount !== 1 ? 's' : ''}
                                </span>
                                {onlineMemberCount > 0 && (
                                    <>
                                        <span className="floating-chat-header-separator">•</span>
                                        <OnlineStatusDot online={true} />
                                        <span className="floating-chat-header-online-count">
                                            {onlineMemberCount} online
                                        </span>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="floating-chat-header-user">
                        <div className="floating-chat-header-avatar-wrapper">
                            {directUser?.photoURL ? (
                                <img
                                    src={directUser.photoURL}
                                    alt={directUser.displayName}
                                    className="floating-chat-header-avatar"
                                />
                            ) : (
                                <div className="floating-chat-header-avatar-placeholder">
                                    {(directUser?.displayName?.[0] || '?').toUpperCase()}
                                </div>
                            )}
                            {isOnline && (
                                <span className="floating-chat-header-online-dot" />
                            )}
                        </div>
                        <div className="floating-chat-header-details">
                            <h3 className="floating-chat-header-name">
                                {directUser?.displayName || 'User'}
                            </h3>
                            {isOnline ? (
                                <span className="floating-chat-header-status floating-chat-header-status--online">
                                    <span>Online</span>
                                </span>
                            ) : (
                                directUser?.userProfession ? (
                                    <span className="floating-chat-header-profession">
                                        {directUser.userProfession}
                                    </span>
                                ) : (
                                    <span className="floating-chat-header-status floating-chat-header-status--offline">
                                        <span>Offline</span>
                                    </span>
                                )
                            )}
                        </div>
                    </div>
                )}
                <button
                    className="floating-chat-close-btn"
                    onClick={handleCloseChat}
                    aria-label="Close chat"
                >
                    ×
                </button>
            </div>
            <ChatWindow hideHeader={true} />
        </div>
    );
};

const ChatManagerCore = () => {
    const [isPanelExpanded, setIsPanelExpanded] = useState(false);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [isCreateGroupOpen, setIsCreateGroupOpen] = useState(false);
    const [groupName, setGroupName] = useState('');
    const [groupMemberQuery, setGroupMemberQuery] = useState('');
    const [selectedGroupMembers, setSelectedGroupMembers] = useState([]);
    const [isCreatingGroup, setIsCreatingGroup] = useState(false);
    const [isOpeningChat, setIsOpeningChat] = useState(false);

    // Use modular contexts instead of legacy useChat
    const {
        activeConversationId,
        setActiveConversationId,
        currentUser,
        openDirectConversationWithUser,
        createGroupConversation,
        conversations
    } = useConversation();
    const location = useLocation();
    const navigate = useNavigate();

    // D8: Extract module-scope constants for maintainability
    const SEARCH_LIMIT = 15;

    // User search for opening direct conversation
    const { results: searchResults, isSearching, reset: resetSearch } = useUserSearch(searchQuery, {
        limit: SEARCH_LIMIT,
        enabled: isSearchOpen && searchQuery.trim().length >= 2
    });

    // FIX #4: Memoize excludeIds to prevent useUserSearch re-execution on every render
    const groupExcludeIds = useMemo(() => {
        const ids = selectedGroupMembers.map(m => m.id);
        return currentUser?.uid ? [currentUser.uid, ...ids] : ids;
    }, [currentUser?.uid, selectedGroupMembers]);

    // User search for adding group members (exclude self and already selected members)
    const { results: groupMemberResults, isSearching: isGroupMemberSearching, reset: resetGroupMemberSearch } = useUserSearch(groupMemberQuery, {
        limit: SEARCH_LIMIT,
        excludeIds: groupExcludeIds,
        enabled: isCreateGroupOpen && groupMemberQuery.trim().length >= 2
    });

    // Auto-open chat from URL parameter (e.g., from profile Message button)
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const convId = params.get('conversationId');
        if (convId && convId !== activeConversationId) {
            setActiveConversationId(convId);
            setIsPanelExpanded(false);
        }
    }, [location.search, activeConversationId, setActiveConversationId]);

    // Calculate total unread count
    const totalUnread = useMemo(() => {
        if (!conversations || !currentUser?.uid) return 0;

        return conversations.reduce((sum, conv) => {
            const lmaRaw = conv.lastMessageAt;
            const lma = lmaRaw?.toDate ? lmaRaw.toDate() : lmaRaw || null;
            const lsaRaw = currentUser.uid && conv.lastSeenAt ? conv.lastSeenAt[currentUser.uid] : null;
            const lsa = lsaRaw?.toDate ? lsaRaw.toDate() : lsaRaw || null;

            const hasMessages = !!(conv.lastMessageText || conv.lastMessagePreview);
            const isUnread = hasMessages && lma && (!lsa || lma > lsa);

            return sum + (isUnread ? 1 : 0);
        }, 0);
    }, [conversations, currentUser]);

    const handleCloseChat = () => {
        setActiveConversationId(null);
        const params = new URLSearchParams(location.search);
        if (params.has('conversationId')) {
            params.delete('conversationId');
            const newSearch = params.toString();
            navigate(location.pathname + (newSearch ? '?' + newSearch : ''), { replace: true });
        }
    };

    const handleTogglePanel = () => {
        setIsPanelExpanded(prev => !prev);
        if (isPanelExpanded) {
            handleCloseChat();
        }
    }

    const handleSearchToggle = (event) => {
        event.stopPropagation();
        setIsSearchOpen(prev => !prev);
        setSearchQuery('');
        resetSearch();
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    const handleUserClick = async (user) => {
        if (!user?.id || !openDirectConversationWithUser || isOpeningChat) return;
        try {
            setIsOpeningChat(true);
            const conversationId = await openDirectConversationWithUser(user.id);
            if (conversationId) {
                setIsSearchOpen(false);
                setSearchQuery('');
                resetSearch();
            }
        } catch (error) {
            console.error('[Chat] Failed to open conversation from search', error);
            showErrorToast('Failed to open chat. Please try again.');
        } finally {
            setIsOpeningChat(false);
        }
    };

    const handleToggleCreateGroup = (event) => {
        event.stopPropagation();
        setIsCreateGroupOpen(prev => !prev);
        setGroupName('');
        setGroupMemberQuery('');
        setSelectedGroupMembers([]);
        resetGroupMemberSearch();
    };

    const handleAddMember = (user) => {
        if (!user?.id) return;
        setSelectedGroupMembers((prev) => {
            if (prev.some((m) => m.id === user.id)) return prev;
            return [...prev, user];
        });
    };

    const handleRemoveMember = (id) => {
        setSelectedGroupMembers((prev) => prev.filter((m) => m.id !== id));
    };

    const MAX_GROUP_NAME_LENGTH = 50;
    const handleCreateGroup = async () => {
        if (!createGroupConversation) return;
        const trimmedName = groupName.trim();
        if (!trimmedName) return;
        // FIX #3: Validate group name length
        if (trimmedName.length > MAX_GROUP_NAME_LENGTH) {
            showErrorToast(`Group name must be ${MAX_GROUP_NAME_LENGTH} characters or less.`);
            return;
        }
        const memberIds = selectedGroupMembers.map((m) => m.id).filter(Boolean);
        if (!memberIds.length) return;
        try {
            setIsCreatingGroup(true);
            const conversationId = await createGroupConversation({ name: trimmedName, memberIds });
            if (conversationId) {
                setIsCreateGroupOpen(false);
                setGroupName('');
                setGroupMemberQuery('');
                setSelectedGroupMembers([]);
                resetGroupMemberSearch();
            }
        } catch (error) {
            console.error('[Chat] Failed to create group conversation', error);
            showErrorToast('Failed to create group. Please try again.');
        } finally {
            setIsCreatingGroup(false);
        }
    };

    // Auto-expand the dock whenever a conversation opens
    useEffect(() => {
        if (activeConversationId && !isPanelExpanded) {
            setIsPanelExpanded(true);
        }
    }, [activeConversationId, isPanelExpanded]);

    return (
        <div className="floating-chat-container">
            {activeConversationId && (
                <ActiveChatWindowWrapper
                    activeConversationId={activeConversationId}
                    conversations={conversations}
                    currentUser={currentUser}
                    handleCloseChat={handleCloseChat}
                />
            )}

            <div
                className={`conversation-panel ${isPanelExpanded ? 'expanded' : 'collapsed'}`}
                onClick={() => !isPanelExpanded && handleTogglePanel()}
            >
                <div className="conversation-panel-header">
                    <span className="text-[14px] text-gray-700 font-bold flex items-center">
                        Messages
                        {totalUnread > 0 && (
                            <span className="chat-unread-badge">{totalUnread}</span>
                        )}
                    </span>
                    {isPanelExpanded && (
                        <div className="conversation-panel-actions">
                            <button
                                type="button"
                                className="conversation-panel-search-btn"
                                onClick={(e) => handleSearchToggle(e)}
                                aria-label={isSearchOpen ? "Close user search" : "Search users"}
                            >
                                <IconSearch size={18} />
                            </button>
                            <button
                                type="button"
                                className="conversation-panel-search-btn"
                                onClick={(e) => handleToggleCreateGroup(e)}
                                aria-label={isCreateGroupOpen ? "Close group creator" : "New group"}
                            >
                                <IconUsers size={18} />
                            </button>
                            <button
                                type="button"
                                className="conversation-panel-minimize-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleTogglePanel();
                                }}
                                aria-label="Minimize"
                            >
                                ×
                            </button>
                        </div>
                    )}
                </div>

                {isPanelExpanded && (
                    <div className="conversation-panel-content" onClick={e => e.stopPropagation()}>
                        {isSearchOpen && (
                            <div className="conversation-panel-search">
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={handleSearchChange}
                                    placeholder="Search users..."
                                    className="conversation-panel-search-input"
                                />
                                <div className="conversation-panel-search-results">
                                    {isSearching && (
                                        <div className="text-center mt-5 text-[12px] text-gray-500 py-1.5">
                                            Searching...
                                        </div>
                                    )}
                                    {!isSearching && searchQuery.trim().length >= 2 && searchResults.length === 0 && (
                                        <div className="text-center mt-5 text-[12px] text-gray-500 py-1.5">
                                            No users found.
                                        </div>
                                    )}
                                    {!isSearching && searchQuery.trim().length < 2 && (
                                        <div className="text-center mt-5 text-[13px] text-gray-500">
                                            Type at least 2 characters to search for users.
                                        </div>
                                    )}
                                    {searchResults.map(user => (
                                        <button
                                            key={user.id}
                                            type="button"
                                            className="conversation-panel-search-item"
                                            onClick={() => handleUserClick(user)}
                                            disabled={isOpeningChat}
                                        >
                                            {isOpeningChat ? (
                                                <span className="conversation-panel-search-item-name" style={{ opacity: 0.6 }}>
                                                    Opening...
                                                </span>
                                            ) : (
                                                <>
                                                    {user.photoURL ? (
                                                        <img
                                                            src={user.photoURL}
                                                            alt={user.displayName || user.username || 'User'}
                                                            className="conversation-search-avatar"
                                                            onError={(e) => { e.target.style.display = 'none'; }}
                                                        />
                                                    ) : (
                                                        <div className="conversation-search-avatar--placeholder">
                                                            {((user.displayName || user.username || user.name || '?')[0]).toUpperCase()}
                                                        </div>
                                                    )}
                                                    <div className="conversation-search-details">
                                                        <span className="conversation-panel-search-item-name">
                                                            {user.displayName || user.username || user.name || 'Unnamed user'}
                                                        </span>
                                                        {user.userProfession && (
                                                            <span className="conversation-panel-search-item-meta">
                                                                {user.userProfession}
                                                            </span>
                                                        )}
                                                    </div>
                                                </>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        {isCreateGroupOpen && (
                            <div className="conversation-panel-group">
                                <input
                                    type="text"
                                    value={groupName}
                                    onChange={(e) => setGroupName(e.target.value)}
                                    placeholder="Group name"
                                    className="conversation-panel-group-input"
                                />
                                <input
                                    type="text"
                                    value={groupMemberQuery}
                                    onChange={(e) => setGroupMemberQuery(e.target.value)}
                                    placeholder="Add members..."
                                    className="conversation-panel-group-input"
                                />
                                <div className="conversation-panel-group-selected">
                                    {selectedGroupMembers.map((user) => (
                                        <button
                                            key={user.id}
                                            type="button"
                                            className="conversation-panel-group-chip"
                                            onClick={() => handleRemoveMember(user.id)}
                                        >
                                            <span className="conversation-panel-group-chip-label">
                                                {user.displayName || user.username || user.name || 'User'}
                                            </span>
                                            <span className="conversation-panel-group-chip-remove">×</span>
                                        </button>
                                    ))}
                                </div>
                                <div className="conversation-panel-group-results">
                                    {isGroupMemberSearching && (
                                        <div className="text-center mt-5 text-[12px] text-gray-500 py-1">Searching...</div>
                                    )}
                                    {!isGroupMemberSearching && groupMemberQuery.trim().length >= 2 && groupMemberResults.length === 0 && (
                                        <div className="text-center mt-5 text-[12px] text-gray-500 py-1">No users found.</div>
                                    )}
                                    {!isGroupMemberSearching && groupMemberQuery.trim().length < 2 && (
                                        <div className="text-center mt-5 text-[13px] text-gray-500">
                                            Type at least 2 characters to search for users.
                                        </div>
                                    )}
                                    {groupMemberResults.map((user) => (
                                        <button
                                            key={user.id}
                                            type="button"
                                            className="conversation-panel-group-item"
                                            onClick={() => handleAddMember(user)}
                                        >
                                            {user.photoURL ? (
                                                <img
                                                    src={user.photoURL}
                                                    alt={user.displayName || user.username || 'User'}
                                                    className="conversation-search-avatar"
                                                    onError={(e) => { e.target.style.display = 'none'; }}
                                                />
                                            ) : (
                                                <div className="conversation-search-avatar--placeholder">
                                                    {((user.displayName || user.username || user.name || '?')[0]).toUpperCase()}
                                                </div>
                                            )}
                                            <div className="conversation-search-details">
                                                <span className="conversation-panel-group-item-name">
                                                    {user.displayName || user.username || user.name || 'Unnamed user'}
                                                </span>
                                                {user.userProfession && (
                                                    <span className="conversation-panel-group-item-meta">{user.userProfession}</span>
                                                )}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                                <button
                                    type="button"
                                    className="conversation-panel-group-create-btn"
                                    onClick={handleCreateGroup}
                                    disabled={isCreatingGroup || !groupName.trim() || !selectedGroupMembers.length}
                                >
                                    {isCreatingGroup ? 'Creating...' : 'Create group'}
                                </button>
                            </div>
                        )}
                        {!isSearchOpen && !isCreateGroupOpen && <ConversationList />}
                    </div>
                )}
            </div>
        </div>
    );
};

const FloatingChatManager = () => {
    // FIX #11: Wire resetKey so error boundary auto-resets on conversation change
    const { activeConversationId } = useConversation();
    return (
        <ChatErrorBoundary resetKey={activeConversationId}>
            <ChatManagerCore />
            <ForwardModal />
        </ChatErrorBoundary>
    );
};

export default FloatingChatManager;
